import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { CHECK_LABELS, PILLAR_LABELS } from './uavsMeta';
import { useTokens } from '../primitives';

const RESULT_RANK = { skip: 0, fail: 1, warn: 2, pass: 3 };

// Diffs the latest audit against the prior audit at check-key granularity:
// new failures (regressions), new passes (wins), and points that moved either
// way. We pull check rows directly off VisibilityScore.pillarScores —
// `getVisibilityScoreHistory` includes pillarScores in its projection.
function diffAudits(current, previous) {
  if (!current || !previous)
    return { regressions: [], wins: [], pointsDelta: 0 };

  const prevByKey = new Map();
  for (const pillar of Object.values(previous.pillarScores || {})) {
    for (const c of pillar?.checkResults || []) prevByKey.set(c.checkKey, c);
  }

  const regressions = [];
  const wins = [];
  let pointsDelta = 0;

  for (const [pillarKey, pillar] of Object.entries(
    current.pillarScores || {}
  )) {
    for (const c of pillar?.checkResults || []) {
      const prev = prevByKey.get(c.checkKey);
      if (!prev) continue;
      const ptDiff = (c.pointsEarned || 0) - (prev.pointsEarned || 0);
      pointsDelta += ptDiff;
      const curRank = RESULT_RANK[c.result] ?? 0;
      const prevRank = RESULT_RANK[prev.result] ?? 0;
      if (curRank < prevRank) {
        regressions.push({ ...c, pillarKey, prevResult: prev.result, ptDiff });
      } else if (curRank > prevRank) {
        wins.push({ ...c, pillarKey, prevResult: prev.result, ptDiff });
      }
    }
  }
  return { regressions, wins, pointsDelta };
}

function ChangeRow({ change, kind }) {
  const t = useTokens();
  const Icon = kind === 'win' ? ArrowUpwardIcon : ArrowDownwardIcon;
  const color = kind === 'win' ? t.greenText : t.red;
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{ py: 0.75 }}
    >
      <Icon sx={{ color, fontSize: 16, mt: 0.25 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {CHECK_LABELS[change.checkKey] || change.checkKey}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {PILLAR_LABELS[change.pillarKey]} · {change.prevResult} →{' '}
          {change.result}
          {change.ptDiff !== 0 && (
            <Box component="span" sx={{ color, fontWeight: 600, ml: 0.5 }}>
              ({change.ptDiff > 0 ? '+' : ''}
              {change.ptDiff} pts)
            </Box>
          )}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function RecentChanges({ history }) {
  const t = useTokens();
  const { current, previous, diff } = useMemo(() => {
    const cur = history?.[0] || null;
    const prev = history?.[1] || null;
    return { current: cur, previous: prev, diff: diffAudits(cur, prev) };
  }, [history]);

  if (!previous || (diff.regressions.length === 0 && diff.wins.length === 0)) {
    return null;
  }

  const prevDate = previous.snapshotAt || previous.createdAt;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          What changed since last audit
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          vs. {new Date(prevDate).toLocaleDateString()}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: t.greenText,
              display: 'block',
              mb: 0.5,
            }}
          >
            Wins ({diff.wins.length})
          </Typography>
          {diff.wins.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No improvements yet — focus on the priorities above.
            </Typography>
          ) : (
            diff.wins
              .slice(0, 5)
              .map((c) => <ChangeRow key={c.checkKey} change={c} kind="win" />)
          )}
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: t.red, display: 'block', mb: 0.5 }}
          >
            Regressions ({diff.regressions.length})
          </Typography>
          {diff.regressions.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Nothing slipped — keep it up.
            </Typography>
          ) : (
            diff.regressions
              .slice(0, 5)
              .map((c) => (
                <ChangeRow key={c.checkKey} change={c} kind="regression" />
              ))
          )}
        </Box>
      </Box>
    </Paper>
  );
}
